/**
 * Iter 97 — one-tap vehicle status switcher (spec §12). Chip on each car
 * card; opens a bottom sheet with the 6 operational statuses; important
 * changes require confirmation.
 */
import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Car, KeyRound, Wrench, AlertTriangle, Ban, CalendarOff, ChevronDown } from "lucide-react";

export const VEHICLE_STATUSES = [
  { key: "available",      label: "Available",      icon: Car,         cls: "bg-emerald-50 text-emerald-700 border-emerald-200", confirm: false },
  { key: "rented",         label: "Rented",         icon: KeyRound,    cls: "bg-blue-50 text-blue-700 border-blue-200",          confirm: false },
  { key: "maintenance",    label: "Maintenance",    icon: Wrench,      cls: "bg-orange-50 text-orange-700 border-orange-200",    confirm: true },
  { key: "accident",       label: "Accident",       icon: AlertTriangle, cls: "bg-rose-50 text-rose-700 border-rose-200",        confirm: true },
  { key: "out_of_service", label: "Out of Service", icon: Ban,         cls: "bg-neutral-100 text-neutral-700 border-neutral-300", confirm: true },
  { key: "blocked",        label: "Blocked",        icon: CalendarOff, cls: "bg-violet-50 text-violet-700 border-violet-200",    confirm: true },
];

const META = Object.fromEntries(VEHICLE_STATUSES.map((s) => [s.key, s]));

export default function VehicleStatusSwitcher({ car }) {
  const [status, setStatus] = useState(car.vehicle_status || "available");
  const [open, setOpen] = useState(false);
  const [pendingKey, setPendingKey] = useState(null);
  const meta = META[status] || META.available;
  const Icon = meta.icon;

  const apply = async (key) => {
    const prev = status;
    setStatus(key);
    setOpen(false);
    setPendingKey(null);
    try {
      await api.patch(`/cars/${car.id}`, { vehicle_status: key });
      toast.success(`${car.make} ${car.model} → ${META[key].label}`);
    } catch (e) {
      setStatus(prev);
      toast.error(formatApiError(e));
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-[11px] font-bold ${meta.cls}`}
        data-testid={`vehicle-status-${car.id}`}
      >
        <Icon className="h-3.5 w-3.5" /> {meta.label} <ChevronDown className="h-3 w-3 opacity-60" />
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="rounded-t-3xl" data-testid="vehicle-status-sheet">
          <SheetHeader className="mb-2">
            <SheetTitle className="text-left">{car.make} {car.model} — status</SheetTitle>
          </SheetHeader>
          <div className="grid grid-cols-2 gap-2 pb-2">
            {VEHICLE_STATUSES.map((s) => {
              const SIcon = s.icon;
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => (s.confirm ? setPendingKey(s.key) : apply(s.key))}
                  className={`flex items-center gap-2 rounded-2xl border p-3.5 text-sm font-bold text-left ${
                    status === s.key ? "ring-2 ring-blue-300" : ""
                  } ${s.cls}`}
                  data-testid={`vehicle-status-option-${s.key}`}
                >
                  <SIcon className="h-4 w-4" /> {s.label}
                </button>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>
      <AlertDialog open={!!pendingKey} onOpenChange={(o) => !o && setPendingKey(null)}>
        <AlertDialogContent data-testid="vehicle-status-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Set {car.make} {car.model} to {pendingKey ? META[pendingKey].label : ""}?</AlertDialogTitle>
            <AlertDialogDescription>
              The vehicle will be flagged as {pendingKey ? META[pendingKey].label.toLowerCase() : ""} on your dashboard counts. You can switch it back any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="vehicle-status-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => apply(pendingKey)} data-testid="vehicle-status-confirm-btn">Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
